import { oauth2Config } from '../../config/oauth2.config';
import { tokenService } from './token.service';
import { generateRandomString, generateCodeChallenge } from '../../utils/crypto';
import axios from 'axios';
import { SocialProvider } from '../../types/auth.types';

export const oauth2Service = {
  getAuthorizationUrl: async (opts?: { provider?: SocialProvider; returnTo?: string }) => {
    const state = generateRandomString(32);
    const codeVerifier = generateRandomString(64);
    const codeChallenge = await generateCodeChallenge(codeVerifier);

    sessionStorage.setItem('oauth2_state', state);
    sessionStorage.setItem('oauth2_code_verifier', codeVerifier);
    if (opts?.returnTo) sessionStorage.setItem('post_auth_redirect', opts.returnTo);

    const params = new URLSearchParams({
      response_type: 'code',
      client_id: oauth2Config.clientId,
      redirect_uri: oauth2Config.redirectUri,
      scope: oauth2Config.scope,
      state,
      code_challenge: codeChallenge,
      code_challenge_method: 'S256',
    });

    // AuthKeystone spec: Use specific social login endpoints
    if (opts?.provider) {
      return `${oauth2Config.serverUrl}/oauth2/login/${opts.provider}?${params.toString()}`;
    }

    return `${oauth2Config.authorizationEndpoint}?${params.toString()}`;
  },

  exchangeCodeForToken: async (code: string, state: string) => {
    const storedState = sessionStorage.getItem('oauth2_state');
    if (!storedState || storedState !== state) {
      throw new Error('OAuth2 state mismatch — possible CSRF attack');
    }

    const codeVerifier = sessionStorage.getItem('oauth2_code_verifier');

    // OAuth2 spec: POST /oauth2/token with application/x-www-form-urlencoded
    const body = new URLSearchParams({
      grant_type: 'authorization_code',
      code,
      client_id: oauth2Config.clientId,
      redirect_uri: oauth2Config.redirectUri,
      ...(codeVerifier ? { code_verifier: codeVerifier } : {}),
    });

    const response = await axios.post(oauth2Config.tokenEndpoint, body, {
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
    });

    sessionStorage.removeItem('oauth2_state');
    sessionStorage.removeItem('oauth2_code_verifier');

    const data = response.data;
    if (!data || !data.access_token) {
      throw new Error('Token endpoint returned an empty or invalid response');
    }

    tokenService.setAccessToken(data.access_token);
    if (data.refresh_token) tokenService.setRefreshToken(data.refresh_token);
    if (data.user) tokenService.setUser(data.user);

    return data;
  },

  // AuthKeystone spec: POST /api/v1/users/login
  authenticateWithCredentials: async (email: string, password: string) => {
    const response = await axios.post(oauth2Config.loginEndpoint, {
      client_id: oauth2Config.clientId,
      email,
      password,
    }, {
      params: { client_id: oauth2Config.clientId }
    });

    const { access_token, refresh_token, user } = response.data;
    tokenService.setAccessToken(access_token);
    if (refresh_token) tokenService.setRefreshToken(refresh_token);
    if (user) tokenService.setUser(user);

    return response.data;
  },

  // AuthKeystone spec: POST /api/v1/organizations/{organizationId}/users/register
  register: async (payload: { name: string; email: string; password: string }) => {
    const response = await axios.post(oauth2Config.registrationEndpoint, {
      client_id: oauth2Config.clientId,
      email: payload.email,
      password: payload.password,
      name: payload.name,
    }, {
      params: { client_id: oauth2Config.clientId }
    });

    // Registration returns User object, not tokens (per AuthKeystone spec)
    // User needs to login after registration
    const { user } = response.data;
    if (user) {
      tokenService.setUser(user);
    }
    return response.data;
  },

  // AuthKeystone spec: POST /api/v1/users/forgot-password (requires client_id)
  forgotPassword: async (email: string) => {
    const response = await axios.post(oauth2Config.forgotPasswordEndpoint, {
      email,
      client_id: oauth2Config.clientId,
    }, {
      params: { client_id: oauth2Config.clientId }
    });
    return response.data;
  },

  // AuthKeystone spec: POST /api/v1/users/reset-password
  resetPassword: async (token: string, newPassword: string) => {
    const response = await axios.post(oauth2Config.resetPasswordEndpoint, {
      token,
      new_password: newPassword,
    }, {
      params: { client_id: oauth2Config.clientId }
    });
    return response.data;
  },

  refreshAccessToken: async () => {
    const refreshToken = tokenService.getRefreshToken();
    if (!refreshToken) throw new Error('No refresh token available');

    // OAuth2 spec: POST /oauth2/token with refresh_token grant
    const body = new URLSearchParams({
      grant_type: 'refresh_token',
      refresh_token: refreshToken,
      client_id: oauth2Config.clientId,
    });

    const response = await axios.post(oauth2Config.tokenEndpoint, body, {
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
    });

    const { access_token, refresh_token: newRefreshToken } = response.data;
    tokenService.setAccessToken(access_token);
    if (newRefreshToken) tokenService.setRefreshToken(newRefreshToken);

    return access_token;
  },

  // AuthKeystone spec: POST /oauth2/revoke
  logout: async () => {
    const token = tokenService.getAccessToken();
    if (token) {
      try {
        const body = new URLSearchParams({
          token,
          client_id: oauth2Config.clientId,
        });
        await axios.post(oauth2Config.revocationEndpoint, body, {
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
          },
        });
      } catch (e) {
        console.error('Logout revocation failed', e);
      }
    }
    tokenService.clear();
  },

  // AuthKeystone spec: GET /oauth2/userinfo
  getUserInfo: async () => {
    const token = tokenService.getAccessToken();
    if (!token) throw new Error('No access token available');

    const response = await axios.get(oauth2Config.userinfoEndpoint, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
      params: { client_id: oauth2Config.clientId }
    });

    return response.data;
  },
};
